/**
 * HeyScent Fragrance Recommendation Engine
 * Central brain for all recommendation touchpoints:
 * - PDP "You may also like"
 * - Cart drawer "Complete Your Routine"
 * - Post-add upsell modal
 *
 * Product data (gender, brand, bestseller) is read from the Liquid-rendered
 * catalog in #hs-product-catalog. Only fragrance family assignments are
 * maintained here, since that data doesn't exist in the store yet.
 */

// Adjacent families score higher than unrelated ones
const ADJACENCY = {
  'fresh-citrus':        ['fresh-masculine', 'floral'],
  'woody-oud':           ['leather-tobacco', 'spicy-warm'],
  'sweet-gourmand':      ['rose-floral-intense', 'bold-statement'],
  'spicy-warm':          ['woody-oud', 'leather-tobacco', 'fresh-masculine'],
  'floral':              ['rose-floral-intense', 'fresh-citrus', 'sweet-gourmand'],
  'leather-tobacco':     ['woody-oud', 'spicy-warm'],
  'fresh-masculine':     ['fresh-citrus', 'spicy-warm'],
  'rose-floral-intense': ['floral', 'sweet-gourmand', 'bold-statement'],
  'bold-statement':      ['sweet-gourmand', 'rose-floral-intense', 'spicy-warm'],
};

/**
 * Fragrance family map: handle → family name.
 * This is the only data maintained in JS — everything else comes from the store.
 * Add new products here when they launch to enable family-based scoring.
 * Products missing from this map still appear in recommendations (via the
 * Liquid catalog) but won't get family/adjacency points.
 */
const FAMILY_MAP = {
  // Fresh / Citrus / Aquatic
  'inspired-by-aventus':              'fresh-citrus',
  'inspired-by-acqua-di-gio-profumo': 'fresh-citrus',
  'inspired-by-cool-water':           'fresh-citrus',
  'inspired-by-green-irish-tweed':    'fresh-citrus',
  'inspired-by-neroli-portofino':     'fresh-citrus',
  'off-duty':                         'fresh-citrus',
  'inspired-by-pacific-chill':        'fresh-citrus',
  'inspired-by-afternoon-swim':       'fresh-citrus',
  'inspired-by-erba-pura':            'fresh-citrus',
  'inspired-by-greenley':             'fresh-citrus',
  'inspired-by-percival':             'fresh-citrus',
  'inspired-by-invictus':             'fresh-citrus',
  'inspired-by-gypsy-water':          'fresh-citrus',
  'el-capitano':                      'fresh-citrus',
  'inspired-by-bleu-de-chanel':       'fresh-citrus',
  'inspired-by-eros':                 'fresh-citrus',

  // Woody / Oud
  'inspired-by-oud-wood':             'woody-oud',
  'inspired-by-ombre-nomade':         'woody-oud',
  'inspired-by-royal-oud':            'woody-oud',
  'inspired-by-oud-for-greatness':    'woody-oud',
  'inspired-by-oud-satin-mood':       'woody-oud',
  'inspired-by-santal-33':            'woody-oud',
  'inspired-by-terre-dhermes':        'woody-oud',
  'inspired-by-velvet-rose-oud':      'woody-oud',
  'inspired-by-limmensite':           'woody-oud',

  // Sweet / Gourmand
  'inspired-by-lost-cherry':          'sweet-gourmand',
  'inspired-by-bianco-latte':         'sweet-gourmand',
  'inspired-by-angel':                'sweet-gourmand',
  'lemon-glaze':                      'sweet-gourmand',
  'inspirerad-by-vanilla28':          'sweet-gourmand',
  'inspired-by-a125':                 'sweet-gourmand',
  'inspired-by-black-phantom':        'sweet-gourmand',
  'inspired-by-love-dont-be-shy':     'sweet-gourmand',
  'inspired-by-la-vie-est-belle':     'sweet-gourmand',
  'inspired-by-good-girl':            'sweet-gourmand',
  'inspired-by-side-effect':          'sweet-gourmand',

  // Spicy / Warm / Oriental
  'inspired-by-sauvage-elixir':       'spicy-warm',
  'inspired-by-stronger-with-you':    'spicy-warm',
  'inspired-by-le-male-elixir':       'spicy-warm',
  'inspired-by-le-male-le-parfum':    'spicy-warm',
  'inspired-by-ultra-male':           'spicy-warm',
  'inspired-by-the-most-wanted':      'spicy-warm',
  'inspired-by-layton':               'spicy-warm',
  'inspired-by-althair':              'spicy-warm',
  'inspired-by-xj-1861-naxos':        'spicy-warm',
  'inspired-by-smoking-hot':          'spicy-warm',
  'inspired-by-fahrenheit':           'spicy-warm',

  // Floral
  'inspired-by-jadore':               'floral',
  'inspired-by-flowerbomb':           'floral',
  'inspired-by-delina':               'floral',
  'inspired-by-flora-gorgeous-gardenia': 'floral',
  'inspired-by-bamboo':               'floral',
  'inspired-by-my-way':               'floral',
  'inspired-by-giorgio-armani-si':     'floral',
  'inspired-by-mojave-ghost':         'floral',
  'inspired-by-dafrique':             'floral',
  'inspired-by-poison-girl':          'floral',

  // Leather / Tobacco / Dark
  'inspired-by-tobacco-vanille':      'leather-tobacco',
  'inspired-by-tuscan-leather':       'leather-tobacco',
  'inspired-by-ombre-leather-parfum': 'leather-tobacco',
  'inspired-by-black-orchid':         'leather-tobacco',
  'inspired-by-black-opium':          'leather-tobacco',
  'inspired-by-black-saffron':        'leather-tobacco',
  'inspired-by-by-the-fireplace':     'leather-tobacco',
  'inspired-by-crystal-noir':         'leather-tobacco',

  // Fresh Masculine Classic
  'inspired-by-sauvage-edt':          'fresh-masculine',
  'inspired-by-boss-bottled':         'fresh-masculine',
  'inspired-by-polo-red':             'fresh-masculine',
  'inspired-by-phantom':              'fresh-masculine',
  'inspired-by-pegasus':              'fresh-masculine',
  'inspired-by-fierce':               'fresh-masculine',
  'inspired-by-carlisle':             'fresh-masculine',
  'inspired-by-noir-extreme-parfum':  'fresh-masculine',
  'inspired-by-homme-intense':        'fresh-masculine',
  'inspired-by-la-nuit-de-lhomme':    'fresh-masculine',

  // Rose / Floral-Intense
  'inspired-by-atomic-rose':          'rose-floral-intense',
  'inspired-by-rose-prick':           'rose-floral-intense',
  'inspired-by-baccarat-rouge-540':   'rose-floral-intense',
  'inspired-by-soleil-blanc':         'rose-floral-intense',
  'inspirerad-by-musk-therapy':       'rose-floral-intense',
  'inspired-by-valaya':               'rose-floral-intense',
  'inspired-by-god-of-fire':          'rose-floral-intense',
  'inspired-by-stellar-times':        'rose-floral-intense',

  // Bold / Statement
  'inspired-by-fucking-fabulous-2':   'bold-statement',
  'inspired-by-bitter-peach':         'bold-statement',
  'inspired-by-black-xs':             'bold-statement',
  'inspired-by-pure-xs-for-her':      'bold-statement',
  'inspired-by-alien':                'bold-statement',
  'inspired-by-lady-million':         'bold-statement',
  'inspired-by-imagination':          'bold-statement',
  'inspired-by-absolu-aventus':       'bold-statement',
  'l63':                              'bold-statement',
};

/**
 * Curated upsell pairings: handle → upsell handle
 * Cross-brand within same/adjacent family, prioritizing bestsellers
 */
const UPSELL_PAIRS = {
  // Fresh / Citrus
  'inspired-by-aventus':              'inspired-by-erba-pura',
  'inspired-by-acqua-di-gio-profumo': 'inspired-by-bleu-de-chanel',
  'inspired-by-cool-water':           'inspired-by-green-irish-tweed',
  'inspired-by-green-irish-tweed':    'inspired-by-aventus',
  'inspired-by-neroli-portofino':     'inspired-by-afternoon-swim',
  'off-duty':                         'inspired-by-bleu-de-chanel',
  'inspired-by-pacific-chill':        'inspired-by-erba-pura',
  'inspired-by-afternoon-swim':       'inspired-by-neroli-portofino',
  'inspired-by-erba-pura':            'inspired-by-aventus',
  'inspired-by-greenley':             'inspired-by-percival',
  'inspired-by-percival':             'inspired-by-greenley',
  'inspired-by-invictus':             'inspired-by-eros',
  'inspired-by-gypsy-water':          'inspired-by-santal-33',
  'el-capitano':                      'inspired-by-erba-pura',
  'inspired-by-bleu-de-chanel':       'inspired-by-sauvage-edt',
  'inspired-by-eros':                 'inspired-by-invictus',

  // Woody / Oud
  'inspired-by-oud-wood':             'inspired-by-oud-for-greatness',
  'inspired-by-ombre-nomade':         'inspired-by-oud-wood',
  'inspired-by-royal-oud':            'inspired-by-oud-wood',
  'inspired-by-oud-for-greatness':    'inspired-by-ombre-nomade',
  'inspired-by-oud-satin-mood':       'inspired-by-baccarat-rouge-540',
  'inspired-by-santal-33':            'inspired-by-gypsy-water',
  'inspired-by-terre-dhermes':        'inspired-by-oud-wood',
  'inspired-by-velvet-rose-oud':      'inspired-by-oud-satin-mood',
  'inspired-by-limmensite':           'inspired-by-ombre-nomade',

  // Sweet / Gourmand
  'inspired-by-lost-cherry':          'inspired-by-bianco-latte',
  'inspired-by-bianco-latte':         'inspired-by-lost-cherry',
  'inspired-by-angel':                'inspired-by-alien',
  'lemon-glaze':                      'inspired-by-erba-pura',
  'inspirerad-by-vanilla28':          'inspired-by-bianco-latte',
  'inspired-by-a125':                 'inspired-by-lost-cherry',
  'inspired-by-black-phantom':        'inspired-by-side-effect',
  'inspired-by-love-dont-be-shy':     'inspired-by-delina',
  'inspired-by-la-vie-est-belle':     'inspired-by-good-girl',
  'inspired-by-good-girl':            'inspired-by-la-vie-est-belle',
  'inspired-by-side-effect':          'inspired-by-black-phantom',

  // Spicy / Warm
  'inspired-by-sauvage-elixir':       'inspired-by-layton',
  'inspired-by-stronger-with-you':    'inspired-by-the-most-wanted',
  'inspired-by-le-male-elixir':       'inspired-by-ultra-male',
  'inspired-by-le-male-le-parfum':    'inspired-by-le-male-elixir',
  'inspired-by-ultra-male':           'inspired-by-le-male-elixir',
  'inspired-by-the-most-wanted':      'inspired-by-stronger-with-you',
  'inspired-by-layton':               'inspired-by-sauvage-elixir',
  'inspired-by-althair':              'inspired-by-layton',
  'inspired-by-xj-1861-naxos':        'inspired-by-tobacco-vanille',
  'inspired-by-smoking-hot':          'inspired-by-xj-1861-naxos',
  'inspired-by-fahrenheit':           'inspired-by-sauvage-elixir',

  // Floral
  'inspired-by-jadore':               'inspired-by-delina',
  'inspired-by-flowerbomb':           'inspired-by-delina',
  'inspired-by-delina':               'inspired-by-jadore',
  'inspired-by-flora-gorgeous-gardenia': 'inspired-by-bamboo',
  'inspired-by-bamboo':               'inspired-by-my-way',
  'inspired-by-my-way':               'inspired-by-giorgio-armani-si',
  'inspired-by-giorgio-armani-si':     'inspired-by-my-way',
  'inspired-by-mojave-ghost':         'inspired-by-dafrique',
  'inspired-by-dafrique':             'inspired-by-mojave-ghost',
  'inspired-by-poison-girl':          'inspired-by-black-opium',

  // Leather / Tobacco
  'inspired-by-tobacco-vanille':      'inspired-by-oud-wood',
  'inspired-by-tuscan-leather':       'inspired-by-ombre-leather-parfum',
  'inspired-by-ombre-leather-parfum': 'inspired-by-tuscan-leather',
  'inspired-by-black-orchid':         'inspired-by-black-opium',
  'inspired-by-black-opium':          'inspired-by-black-orchid',
  'inspired-by-black-saffron':        'inspired-by-by-the-fireplace',
  'inspired-by-by-the-fireplace':     'inspired-by-tobacco-vanille',
  'inspired-by-crystal-noir':         'inspired-by-black-opium',

  // Fresh Masculine
  'inspired-by-sauvage-edt':          'inspired-by-bleu-de-chanel',
  'inspired-by-boss-bottled':         'inspired-by-fierce',
  'inspired-by-polo-red':             'inspired-by-phantom',
  'inspired-by-phantom':              'inspired-by-polo-red',
  'inspired-by-pegasus':              'inspired-by-carlisle',
  'inspired-by-fierce':               'inspired-by-boss-bottled',
  'inspired-by-carlisle':             'inspired-by-layton',
  'inspired-by-noir-extreme-parfum':  'inspired-by-sauvage-edt',
  'inspired-by-homme-intense':        'inspired-by-la-nuit-de-lhomme',
  'inspired-by-la-nuit-de-lhomme':    'inspired-by-homme-intense',

  // Rose / Floral-Intense
  'inspired-by-atomic-rose':          'inspired-by-baccarat-rouge-540',
  'inspired-by-rose-prick':           'inspired-by-atomic-rose',
  'inspired-by-baccarat-rouge-540':   'inspired-by-lost-cherry',
  'inspired-by-soleil-blanc':         'inspired-by-baccarat-rouge-540',
  'inspirerad-by-musk-therapy':       'inspired-by-baccarat-rouge-540',
  'inspired-by-valaya':               'inspired-by-delina',
  'inspired-by-god-of-fire':          'inspired-by-stellar-times',
  'inspired-by-stellar-times':        'inspired-by-god-of-fire',

  // Bold / Statement
  'inspired-by-fucking-fabulous-2':   'inspired-by-tobacco-vanille',
  'inspired-by-bitter-peach':         'inspired-by-lost-cherry',
  'inspired-by-black-xs':             'inspired-by-sauvage-elixir',
  'inspired-by-pure-xs-for-her':      'inspired-by-lady-million',
  'inspired-by-alien':                'inspired-by-angel',
  'inspired-by-lady-million':         'inspired-by-pure-xs-for-her',
  'inspired-by-imagination':          'inspired-by-baccarat-rouge-540',
  'inspired-by-absolu-aventus':       'inspired-by-aventus',
  'l63':                              'inspired-by-baccarat-rouge-540',
};

/**
 * Runtime product catalog — built from Liquid JSON + AJAX at init.
 * Structure: handle → { family, gender, brand, bestseller }
 */
const PRODUCTS = {};

/**
 * Parse a tags array (lowercase strings) into gender, bestseller.
 */
function parseTags(tags) {
  const hasHim = tags.includes('him');
  const hasHer = tags.includes('her');
  let gender = 'unisex';
  if (hasHim && !hasHer) gender = 'him';
  else if (hasHer && !hasHim) gender = 'her';
  return { gender, bestseller: tags.includes('bestseller') };
}

/**
 * Extract brand name from tags like "brand-tom-ford" → "Tom Ford".
 */
function brandFromTags(tags) {
  for (const tag of tags) {
    if (tag.startsWith('brand-')) {
      return tag.slice(6).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
  }
  return null;
}

/**
 * Add a product to the catalog (skips if already present).
 * Also skips non-fragrance products (no gender tag AND no FAMILY_MAP entry).
 */
function addProduct(handle, tags, brand) {
  if (PRODUCTS[handle]) return;
  // Kommande lanseringar products cannot be bought yet; both catalog sources (the Liquid JSON and /products.json)
  // pass here, so this one skip keeps them out of every engine recommendation.
  if (tags.includes('kommande')) return;
  const { gender, bestseller } = parseTags(tags);
  const family = FAMILY_MAP[handle] || null;

  // Skip non-fragrance items: no gender tag and no family assignment
  const hasGenderTag = tags.includes('him') || tags.includes('her');
  if (!family && !hasGenderTag) return;

  // Use provided brand, fall back to brand-* tag, fall back to "Unknown"
  const resolvedBrand = (brand && brand.trim() !== 'My Store' ? brand : brandFromTags(tags)) || 'Unknown';

  PRODUCTS[handle] = { family, gender, brand: resolvedBrand.trim(), bestseller };
  if (!family) {
    console.warn(`[HeyScent] No family assigned for "${handle}" — add it to FAMILY_MAP`);
  }
}

/**
 * Build the runtime catalog.
 * Phase 1 (sync): Read the Liquid-rendered JSON in #hs-product-catalog (first 50 products).
 * Phase 2 (async): Fetch /products.json to get the full catalog (~80+ products).
 * Products from Liquid have better brand names (from metafields), so they take priority.
 */
function initCatalog() {
  // Phase 1: Liquid catalog (sync, immediate, first 50 products)
  const el = document.getElementById('hs-product-catalog');
  if (el) {
    try {
      const items = JSON.parse(el.textContent);
      for (const item of items) {
        const tags = item.tags.map(t => t.toLowerCase());
        addProduct(item.handle, tags, item.brand);
      }
      console.log(`[HeyScent] Liquid catalog: ${Object.keys(PRODUCTS).length} products`);
    } catch (e) {
      console.error('[HeyScent] Failed to parse Liquid catalog:', e);
    }
  } else {
    console.warn('[HeyScent] No Liquid catalog found');
  }

  // Phase 2: AJAX fetch for full catalog (async, fills gaps beyond first 50)
  fetch('/products.json?limit=250')
    .then(r => r.json())
    .then(data => {
      const before = Object.keys(PRODUCTS).length;
      for (const product of data.products) {
        const rawTags = Array.isArray(product.tags) ? product.tags : (product.tags || '').split(',');
        const tags = rawTags.map(t => String(t).trim().toLowerCase()).filter(Boolean);
        addProduct(product.handle, tags, product.vendor);
      }
      const after = Object.keys(PRODUCTS).length;
      if (after > before) {
        console.log(`[HeyScent] AJAX catalog: +${after - before} products (total: ${after})`);
      }
    })
    .catch(e => console.warn('[HeyScent] AJAX catalog fetch failed:', e));
}

/**
 * Check if two genders are compatible for recommendations.
 * Unisex matches everything. Same gender matches. Otherwise no.
 */
function genderCompatible(g1, g2) {
  if (g1 === 'unisex' || g2 === 'unisex') return true;
  return g1 === g2;
}

/**
 * Score a candidate product against a source product.
 *
 * Scoring (out of 65 max):
 *   Bestseller:       +25  (prioritize bestsellers)
 *   Same family:      +20  (core relevance)
 *   Adjacent family:  +10  (related scents)
 *   Same brand:       +15  (brand affinity)
 *   Cross-brand:       +5  (variety bonus)
 *   Gender mismatch:   -1  (skip)
 */
function scoreProduct(source, candidate) {
  if (!genderCompatible(source.gender, candidate.gender)) return -1;

  let score = 0;

  // Bestseller: +25
  if (candidate.bestseller) {
    score += 25;
  }

  // Family scoring (only when both products have family assigned)
  if (source.family && candidate.family) {
    if (source.family === candidate.family) {
      score += 20;
    } else if (ADJACENCY[source.family]?.includes(candidate.family)) {
      score += 10;
    }
  }

  // Brand scoring (case-insensitive — Liquid and AJAX may use different casing)
  if (source.brand.toLowerCase() === candidate.brand.toLowerCase()) {
    score += 15;
  } else {
    score += 5;
  }

  return score;
}

/**
 * Get recommendations for a single product.
 * @param {string} handle - Product handle
 * @param {Object} options
 * @param {number} [options.limit=8] - Max results
 * @param {string[]} [options.exclude=[]] - Handles to exclude
 * @returns {string[]} Sorted array of recommended handles
 */
function getRecommendations(handle, { limit = 8, exclude = [] } = {}) {
  const source = PRODUCTS[handle];
  if (!source) return [];

  const excludeSet = new Set(exclude);
  excludeSet.add(handle);

  const scored = [];

  for (const [candidateHandle, candidateData] of Object.entries(PRODUCTS)) {
    if (excludeSet.has(candidateHandle)) continue;
    const s = scoreProduct(source, candidateData);
    if (s >= 0) {
      scored.push({ handle: candidateHandle, score: s });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.handle);
}

/**
 * Get recommendations based on ALL items in cart.
 * Scores each candidate against every cart item, takes the max score.
 * @param {string[]} cartHandles - Array of product handles in cart
 * @param {Object} options
 * @param {number} [options.limit=6] - Max results
 * @returns {string[]} Sorted array of recommended handles
 */
function getCartRecommendations(cartHandles, { limit = 6 } = {}) {
  if (!cartHandles || cartHandles.length === 0) return [];

  const cartSet = new Set(cartHandles);
  const sources = cartHandles.map((h) => PRODUCTS[h]).filter(Boolean);
  if (sources.length === 0) return [];

  const scored = [];

  for (const [candidateHandle, candidateData] of Object.entries(PRODUCTS)) {
    if (cartSet.has(candidateHandle)) continue;

    // Take the best score across all cart items
    let bestScore = -1;
    for (const source of sources) {
      const s = scoreProduct(source, candidateData);
      if (s > bestScore) bestScore = s;
    }

    if (bestScore >= 0) {
      scored.push({ handle: candidateHandle, score: bestScore });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.handle);
}

/**
 * Get the curated upsell for a product.
 * @param {string} handle - Product handle
 * @returns {string|null} Upsell product handle, or null
 */
function getUpsell(handle) {
  return UPSELL_PAIRS[handle] || null;
}

/**
 * Check if a handle is a known fragrance product.
 * @param {string} handle
 * @returns {boolean}
 */
function isKnownProduct(handle) {
  return handle in PRODUCTS;
}

// Initialize catalog from Liquid data on load
initCatalog();

// Export via window.HeyScent
window.HeyScent = {
  getRecommendations,
  getCartRecommendations,
  getUpsell,
  isKnownProduct,
  PRODUCTS,
};
